import Image from 'next/legacy/image';
import styled from 'styled-components';
import { Attribution, AttributionLink, Box, Heading, Paragraph } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, typography } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { GridLayout } from '@99designs/marketing-ui';
import { linkTo } from '@99designs/routing';
import { useTrackCTALinkClicked } from '../../../lib/events';
import { ProductLandingGroupKey } from '../../../lib/productLandingGroups';
import { ReasonsWrapper } from '../../ReasonsWrapper/ReasonsWrapper';
import { getReasons } from './getReasons';
import type { ZendeskLanguage } from '@99designs/routing';
import type { Why99dDesignerInfo } from '../../../lib/productLandingGroups';

type Why99dSectionProps = {
    title: string;
    categoryKey: ProductLandingGroupKey;
    designerInfo: Why99dDesignerInfo;
    language: ZendeskLanguage;
};

const StyledParagraph = styled(Paragraph)`
    ${mediaQuery.minimum('normal')} {
        font-size: ${typography.typeScale.size4};
    }
`;

export const Why99dSection = ({
    title,
    categoryKey,
    designerInfo,
    language,
}: Why99dSectionProps) => {
    const reasons = getReasons(language);
    const designerAttributeCTARef = useTrackCTALinkClicked({
        category: categoryKey,
        ctaLocation: 'why 99designs section',
        ctaButton: 'designer attribution link',
    });

    return (
        <GridLayout>
            <Box
                background={colors.white}
                display="flex"
                flexDirection={['column-reverse', 'column-reverse', 'column-reverse', 'row']}
                paddingY={['3bu', '3bu', '4bu', '6bu']}
            >
                <Box
                    width={['inherit', 'inherit', 'inherit', '40%']}
                    display="flex"
                    flexDirection="column"
                    alignItems={['center', 'center', 'center', 'start']}
                >
                    <Box
                        position="relative"
                        height={['270px', '450px', '450px', '450px']}
                        width={['262.8px', '438px', '438px', '438px']}
                        display="block"
                    >
                        <Image
                            src={designerInfo.image}
                            alt={formatMessage(__('Design by {displayName}'), {
                                displayName: designerInfo.name,
                            })}
                            objectFit="cover"
                            layout="fill"
                            quality={75}
                        />
                    </Box>
                    <Box
                        paddingTop="0.75bu"
                        display="flex"
                        flexDirection="column"
                        alignItems="end"
                        width={['262.8px', '438px', '438px', '438px']}
                    >
                        <Attribution>
                            <AttributionLink
                                ref={designerAttributeCTARef}
                                href={linkTo.userProfile(designerInfo.id)}
                            >
                                {formatMessage(__('by {designer}'), {
                                    designer: designerInfo.name,
                                })}
                            </AttributionLink>
                        </Attribution>
                    </Box>
                </Box>
                <Box
                    width={['inherit', 'inherit', 'inherit', '60%']}
                    paddingLeft={['none', 'none', 'none', '2bu']}
                >
                    <Heading accessibilityLevel={2} appearanceLevel={3}>
                        {title}
                    </Heading>
                    <Box paddingTop={['1bu', '1.5bu']}>
                        <StyledParagraph>
                            {__('We live and breathe design, and folks think that’s pretty rad.')}
                        </StyledParagraph>
                    </Box>
                    <ReasonsWrapper reasons={reasons} />
                </Box>
            </Box>
        </GridLayout>
    );
};
